import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getAddress, isAddress, type Address } from 'viem'
import { usePublicClient } from 'wagmi'
import { getTokens } from '../api/client'
import type { ListedTokenJson, TokenListResponse } from '../api/types'
import { erc20Abi } from '../lib/abis'
import { shortAddress } from '../lib/format'
import { DEFAULT_TOKENS, NATIVE_TOKEN, type TokenMeta } from '../lib/tokens'

/**
 * The token picker's list: `GET /tokens`, plus anything the user has pasted.
 *
 * The API's list is every token in at least one pool the engine would route through — its
 * own candidate filter, echoed back as `filter` — ordered by the TVL of those pools. Until it
 * answers, and in its place if it cannot, the picker offers the base tokens from
 * `DEFAULT_TOKENS`, so a slow or unreachable API still leaves a working card. Once it has
 * answered it is the list: a base token the API no longer lists is not put back.
 *
 * A pasted token's metadata is read from the contract, never guessed — `decimals` in
 * particular, because a wrong one silently scales the amount by a power of ten and the API
 * has no way to tell that `1000000` meant one USDC and not one wei of it. Two tokens on
 * this chain also report the symbol `WFLR` and three report `FXRP`, so the address is the
 * identity throughout and the symbol is a label.
 *
 * Pasting a token the API does not list is still allowed. It will almost certainly not
 * route — no qualifying pool touches it — but the list can lag the chain by a minute, and
 * the quote is what gives the real answer. `isListed` lets the picker say which is which.
 */

const STORAGE_KEY = 'sparkdex.customTokens'

/** The API serves one database read per minute; asking more often returns the same list. */
const LIST_STALE_MS = 60_000
const LIST_REFRESH_MS = 5 * 60_000

function loadCustom(): TokenMeta[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(isTokenMeta).map((token) => ({ ...token, address: getAddress(token.address) }))
  } catch {
    return []
  }
}

function isTokenMeta(value: unknown): value is TokenMeta {
  if (typeof value !== 'object' || value === null) return false
  const token = value as Record<string, unknown>
  return (
    typeof token.address === 'string' &&
    isAddress(token.address) &&
    typeof token.symbol === 'string' &&
    typeof token.name === 'string' &&
    typeof token.decimals === 'number'
  )
}

/**
 * A listed row as the picker's `TokenMeta`. A row this app cannot use safely — a malformed
 * address, or decimals outside a uint8 — is dropped rather than offered: the amount field
 * parses against `decimals`, so a bad value there is a mis-scaled trade.
 */
function fromListed(row: ListedTokenJson): TokenMeta | null {
  if (row.native) return { ...NATIVE_TOKEN, symbolCollision: row.symbolCollision }
  if (!isAddress(row.address, { strict: false })) return null
  if (!Number.isInteger(row.decimals) || row.decimals < 0 || row.decimals > 255) return null
  const address = getAddress(row.address)
  return {
    address,
    symbol: row.symbol ?? shortAddress(address),
    name: row.name ?? '',
    decimals: row.decimals,
    symbolCollision: row.symbolCollision,
  }
}

export type TokenListState = {
  tokens: TokenMeta[]
  /**
   * Where the list came from. `live` is the API's; `loading` and `unavailable` are the
   * base-token fallback, and `listError` says why for the second.
   */
  status: 'loading' | 'live' | 'unavailable'
  listError: Error | null
  /** The API's filter, once it has answered: what "listed" means. */
  filter: TokenListResponse['filter'] | null
  /** In the API's list. False for everything while the list is not live. */
  isListed: (address: Address) => boolean
  find: (address: Address) => TokenMeta | null
  /** Reads a pasted address off chain. Rejects anything without the ERC20 surface. */
  resolve: (address: string) => Promise<TokenMeta>
  add: (token: TokenMeta) => void
  isCustom: (address: Address) => boolean
}

export function useTokenList(): TokenListState {
  const publicClient = usePublicClient()
  const [custom, setCustom] = useState<TokenMeta[]>(() => loadCustom())

  const query = useQuery<TokenListResponse, Error>({
    queryKey: ['tokens'],
    queryFn: ({ signal }) => getTokens(signal),
    staleTime: LIST_STALE_MS,
    refetchInterval: LIST_REFRESH_MS,
    refetchIntervalInBackground: false,
  })

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(custom))
    } catch {
      // Storage disabled. The list still works for this session.
    }
  }, [custom])

  const listed = useMemo(
    () => (query.data ? query.data.tokens.map(fromListed).filter((token): token is TokenMeta => token !== null) : null),
    [query.data]
  )
  const listedAddresses = useMemo(() => new Set(listed?.map((token) => token.address) ?? []), [listed])

  const tokens = useMemo(() => {
    const primary = listed ?? DEFAULT_TOKENS
    const seen = new Set(primary.map((token) => token.address))
    return [...primary, ...custom.filter((token) => !seen.has(token.address))]
  }, [listed, custom])

  const find = useCallback(
    (address: Address) => tokens.find((token) => token.address === getAddress(address)) ?? null,
    [tokens]
  )

  const resolve = useCallback(
    async (input: string): Promise<TokenMeta> => {
      if (!isAddress(input)) throw new Error('that is not a 20-byte address')
      const address = getAddress(input)
      const known = find(address)
      if (known) return known
      if (!publicClient) throw new Error('no RPC client')

      const [symbol, name, decimals] = await publicClient.multicall({
        allowFailure: false,
        contracts: [
          { address, abi: erc20Abi, functionName: 'symbol' },
          { address, abi: erc20Abi, functionName: 'name' },
          { address, abi: erc20Abi, functionName: 'decimals' },
        ],
      })

      return { address, symbol, name, decimals: Number(decimals) }
    },
    [find, publicClient]
  )

  const add = useCallback((token: TokenMeta) => {
    setCustom((current) => (current.some((entry) => entry.address === token.address) ? current : [...current, token]))
  }, [])

  const isCustom = useCallback((address: Address) => custom.some((token) => token.address === address), [custom])
  const isListed = useCallback((address: Address) => listedAddresses.has(address), [listedAddresses])

  return {
    tokens,
    status: listed ? 'live' : query.isPending ? 'loading' : 'unavailable',
    listError: query.error,
    filter: query.data?.filter ?? null,
    isListed,
    find,
    resolve,
    add,
    isCustom,
  }
}
